<template>
  <label v-if="showButton" :class="buttonClass">
    <slot name="button">Upload</slot>
    <input
      ref="fileInput"
      type="file"
      class="w-0 h-0 overflow-hidden"
      :accept="props.mimeTypes?.join(',') || '*'"
      :multiple="props.multiple"
      @change="uploadFile"
    />
  </label>
  <div v-bind="$attrs" ref="dropZoneRef" class="relative">
    <slot
      name="default"
      :select-file="selectFile"
      :is-over-drop-zone="isOverDropZone"
      :uploader="uploader"
    ></slot>

    <Teleport to="body">
      <ImageCropper
        v-if="imagePreviewUrl"
        :src="imagePreviewUrl"
        :file-type="imageFileType"
        :file-name="fileName"
        :sizes="cropperSizes"
        @close="imagePreviewUrl = ''"
        @done="uploadCroppedFile"
      />
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import type { UploadState } from "@/composables/s3/useS3Uploader";
import type { FileCategory } from "@/types/files";
import type { OrientationSizeMap } from "@/utils/platformImageSpecs";
import { useToast } from "vue-toast-notification";
import Compressor from "compressorjs";

const ImageCropper = defineAsyncComponent(() => import("../shared/ImageCropper.vue"));

const props = defineProps<{
  multiple?: boolean;
  max?: number;
  maxFileSize?: number;
  uploadType?: FileCategory;
  mimeTypes?: string[];
  buttonClass?: string;
  showButton?: boolean;
  enableCropper?: boolean;
  cropperSizes?: OrientationSizeMap;
}>();

const imagePreviewUrl = ref("");
const imageFileType = ref("");
const fileName = ref("");
const dropZoneRef = ref<HTMLDivElement>();
const uploader = useS3Uploader({
  mimeTypes: props.mimeTypes,
  uploadType: props.uploadType,
});
const fileInput = ref<HTMLInputElement>();
const $toast = useToast();
const { t } = withI18nHelpers();

const emit = defineEmits<{
  (e: "uploadSuccess", data: UploadState): void;
}>();

const { isOverDropZone } = useDropZone(dropZoneRef, {
  onDrop: cropOrUploadFile,
});

function selectFile() {
  if (fileInput.value) {
    fileInput.value.click();
  }
}

function cropOrUploadFile(files: File[] | null) {
  if (!files) return;
  const file = files[0];
  if (!file) return;

  const mimeAllowed = !props.mimeTypes?.length || props.mimeTypes.includes(file.type);
  if (props.enableCropper && file.type.startsWith("image/") && mimeAllowed) {
    const url = getFilePreviewUrl(file);
    imageFileType.value = file.type;
    imagePreviewUrl.value = url;
    fileName.value = file.name;
  } else {
    uploader.handleFileInput(files);
  }
}

function uploadFile(event: Event) {
  const fileList = (event.target as HTMLInputElement).files;
  if (!fileList) return;
  const maxFiles = props.max ?? 5;
  if (fileList.length > maxFiles) {
    $toast.error(t("maxFilesExceeded", { max: maxFiles }), defaultToastOptions);
    return;
  }
  const files = Array.from(fileList);
  cropOrUploadFile(files);
}

function maybeCompressFile(file: File, cb: (result: File) => void) {
  if (!file) return;
  // skip compression for non-images or files smaller than maxFileSize
  if (
    !props.maxFileSize ||
    file.size <= props.maxFileSize ||
    !file.type.startsWith("image/")
  ) {
    cb(file);
    return;
  }
  new Compressor(file, {
    convertSize: props.maxFileSize,
    success(result) {
      const compressedFile = new File([result], (result as File).name || file.name, {
        type: result.type,
      });
      cb(compressedFile);
    },
  });
}

function uploadCroppedFile(file: File) {
  if (!file) return;
  imagePreviewUrl.value = "";
  imageFileType.value = "";
  fileName.value = "";
  maybeCompressFile(file, uploaderFile => {
    uploader.handleFileInput([uploaderFile]);
  });
}

defineExpose({
  uploader,
  selectFile,
  imagePreviewUrl,
  isOverDropZone,
});

// @ts-ignore vue is messing up the types
watch(uploader.uploadRecently, (newUploads: UploadState[]) => {
  if (newUploads.length) {
    const processedIds = newUploads.map(item => {
      emit("uploadSuccess", item);
      return item.id;
    });

    nextTick(() => {
      uploader.uploadRecently.value = uploader.uploadRecently.value.filter(
        item => !processedIds.includes(item.id)
      );
    });
  }
});
</script>
